import PatientPhotoAccess from "@/features/clinical-actions/PatientPhotoAccess.jsx";
// src/pages/patient/PatientPhotos.jsx //
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext.jsx";

import Card from "@/components/ui/Card.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import BasicDialogCritical from "@/components/ui/BasicDialogCritical.jsx";

// Icons (reuse doctor icons for now — they exist + consistent)
const ICON_BACK = "/icons/doctor/arrow_back.svg";
const ICON_CLOSE = "/icons/doctor/close.svg";
const ICON_ADD = "/icons/doctor/add_2.svg";
const ICON_EDIT = "/icons/doctor/edit_square.svg";
const ICON_DOWNLOAD_BOTTOM = "/icons/doctor/download.svg";
const ICON_DELETE_BOTTOM = "/icons/doctor/delete_critical.svg";
const ICON_CHECK = "/icons/doctor/check_mark.svg";

const ICON_UPLOAD_IMAGE = "/icons/doctor/imagesmode.svg";
const ICON_TAKE_PHOTO = "/icons/doctor/photo_camera.svg";
const ICON_CHEVRON_RIGHT = "/icons/doctor/navigate_next.svg";

// Viewer arrows (you already use these in doctor-side viewer)
const ICON_NEXT = "/icons/doctor/appointments/arrow_next.svg";
const ICON_BEFORE = "/icons/doctor/appointments/arrow_before.svg";

// Demo fallback (same set used elsewhere)
const PATIENT_PHOTOS_1 = "/icons/doctor/appointments/skin-rash.jpg";
const PATIENT_PHOTOS_2 = "/icons/doctor/appointments/skin-rash_scroll.jpg";
const PATIENT_PHOTOS_3 = "/icons/doctor/appointments/symptoms of monkeypox disease.jpg";
const PATIENT_PHOTOS_4 = "/icons/doctor/appointments/red spots_4295.jpg";
const PATIENT_PHOTOS_5 = "/icons/doctor/appointments/acne scars_6929.jpg";
const PATIENT_PHOTOS_6 = "/icons/doctor/appointments/acne scars_0582.jpg";
const PATIENT_PHOTOS_7 = "/icons/doctor/appointments/monkeypox disease_3737.jpg";
const PATIENT_PHOTOS_8 = "/icons/doctor/appointments/monkeypox disease_8061.jpg";

// ✅ NEW patient key
const PATIENT_PHOTOS_KEY_PREFIX = "afrimed_patient_photos_v1:";
const PATIENT_BUCKET = "patient-photos";
const PATIENT_PHOTO_READ_STATUS = Object.freeze({
  LOADING: "loading",
  WITH_IMAGES: "resolved-with-images",
  EMPTY: "resolved-empty",
  FAILED: "read-failed",
});

function getPatientPhotosKeys(profile, user) {
  const ids = [
    profile?.id,
    profile?.user_id,
    profile?.uid,
    profile?.patient_id,
    user?.id,
  ]
    .map((v) => String(v || "").trim())
    .filter(Boolean);

  const uniqueIds = Array.from(new Set(ids));
  return uniqueIds.map((id) => `${PATIENT_PHOTOS_KEY_PREFIX}${id}`);
}

function isDevBypassIdentity(user) {
  return (
    String(user?.id || "").trim() === "dev-user" ||
    user?.app_metadata?.bypass_dev_identity === true ||
    String(user?.user_metadata?.bypass_dev_identity || "").trim() === "true"
  );
}

function canUseLocalPatientPhotos(user) {
  if (typeof window === "undefined") return false;
  if (!allowMockPhotos()) return false;
  return !user?.id || isDevBypassIdentity(user);
}

function readPatientPhotosFromStorage(profile, user) {
  if (typeof window === "undefined") return null;
  if (!canUseLocalPatientPhotos(user)) return null;
  try {
    const keys = getPatientPhotosKeys(profile, user);
    const raw =
      keys
        .map((key) => window.localStorage.getItem(key))
        .find(Boolean) || null;
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const arr = Array.isArray(parsed)
      ? parsed
      : parsed && Array.isArray(parsed.images)
      ? parsed.images
      : [];

    if (!arr.length) return null;

    return arr
      .map((item, idx) => {
        const url =
          typeof item === "string"
            ? item
            : item.url || item.image_url || item.src || "";
        const path = typeof item === "object" && item ? item.path || item.storage_path || "" : "";
        return url ? { id: item.id || `img-${idx}`, url, path } : null;
      })
      .filter(Boolean);
  } catch {
    return null;
  }
}

function clearPatientPhotosStorage(profile, user) {
  if (typeof window === "undefined") return;
  try {
    getPatientPhotosKeys(profile, user).forEach((key) => {
      window.localStorage.removeItem(key);
    });
  } catch {
    // Non-fatal local cache miss.
  }
}

function extFromFile(file) {
  const name = String(file?.name || "");
  const dot = name.lastIndexOf(".");
  if (dot > -1) return name.slice(dot + 1).toLowerCase();

  const type = String(file?.type || "").toLowerCase();
  if (type.includes("png")) return "png";
  if (type.includes("webp")) return "webp";
  if (type.includes("jpeg") || type.includes("jpg")) return "jpg";
  return "jpg";
}

function isDemoImageId(id) {
  return String(id || "").startsWith("img-");
}

function envTrue(v) {
  const s = String(v ?? "").trim().toLowerCase();
  return s === "1" || s === "true" || s === "yes" || s === "on";
}

function allowMockPhotos() {
  if (!import.meta.env.DEV) return false;

  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isLocal =
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "0.0.0.0" ||
    host.endsWith(".local");
  if (!isLocal) return false;

  return (
    envTrue(import.meta.env.VITE_DEV_ONBOARDING) ||
    envTrue(import.meta.env.VITE_BYPASS_AUTH)
  );
}

function useInitialImages(profile, user) {
  const stored = readPatientPhotosFromStorage(profile, user);
  if (stored && stored.length) return stored;

  if (!canUseLocalPatientPhotos(user)) return [];

  const list = [
    PATIENT_PHOTOS_1,
    PATIENT_PHOTOS_2,
    PATIENT_PHOTOS_3,
    PATIENT_PHOTOS_4,
    PATIENT_PHOTOS_5,
    PATIENT_PHOTOS_6,
    PATIENT_PHOTOS_7,
    PATIENT_PHOTOS_8,
  ];

  return list.map((url, idx) => ({ id: `img-${idx}`, url }));
}

export default function PatientPhotos() {
  const nav = useNavigate();
  const location = useLocation();
  // not strictly needed, but keeps parity with your app pattern
  const { profile, user } = useAuth() || {};

  const [images, setImages] = React.useState(() => useInitialImages(profile, user));
  const [photoReadStatus, setPhotoReadStatus] = React.useState(() =>
    images.length
      ? PATIENT_PHOTO_READ_STATUS.WITH_IMAGES
      : PATIENT_PHOTO_READ_STATUS.LOADING,
  );

  const [selectionMode, setSelectionMode] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState("");

  const [bottomSheetOpen, setBottomSheetOpen] = React.useState(false);
  const fileInputRef = React.useRef(null);

  // Viewer
  const [viewerOpen, setViewerOpen] = React.useState(false);
  const [viewerIndex, setViewerIndex] = React.useState(0);
  const [syncing, setSyncing] = React.useState(false);
  const handledEntryActionRef = React.useRef("");

  const selectedCount = selectedIds.size;
  const stableProfileKey = [
    profile?.id || "",
    profile?.user_id || "",
    profile?.uid || "",
    profile?.patient_id || "",
    user?.id || "",
  ].join("|");

  const persistImagesLocal = React.useCallback(
    (nextImages) => {
      if (typeof window === "undefined") return;
      const payload = nextImages.map((img) => ({
        id: img.id,
        url: img.url,
        path: img.path || "",
      }));
      if (!canUseLocalPatientPhotos(user)) return;
      const keys = getPatientPhotosKeys(profile, user);
      keys.forEach((key) => {
        window.localStorage.setItem(key, JSON.stringify(payload));
      });
    },
    [stableProfileKey]
  );

  const persistPhotoPathsToDb = React.useCallback(async (nextImages) => {
    const paths = (Array.isArray(nextImages) ? nextImages : [])
      .map((img) => String(img?.path || "").trim())
      .filter(Boolean);

    const { default: supabase } = await import("@/services/supabaseClient");
    const {
      data: { user },
      error: authErr,
    } = await supabase.auth.getUser();

    if (authErr || !user?.id) {
      throw new Error("Please sign in to save patient photos.");
    }

    const { error: saveErr } = await supabase
      .from("patient_profiles")
      .upsert(
        { user_id: user.id, patient_photo_paths: paths },
        { onConflict: "user_id" }
      );

    if (saveErr) {
      const msg = String(saveErr.message || "");
      if (
        msg.includes("patient_photo_paths") &&
        msg.toLowerCase().includes("column")
      ) {
        return;
      }
      throw saveErr;
    }
  }, []);

  React.useEffect(() => {
    let alive = true;

    async function hydrateFromDb() {
      const hasLocalMock = canUseLocalPatientPhotos(user) && images.length > 0;
      if (!hasLocalMock) setPhotoReadStatus(PATIENT_PHOTO_READ_STATUS.LOADING);

      try {
        const { default: supabase } = await import("@/services/supabaseClient");
        const {
          data: { user },
          error: authErr,
        } = await supabase.auth.getUser();

        if (authErr || !user?.id) {
          if (!alive) return;
          setPhotoReadStatus(
            hasLocalMock
              ? PATIENT_PHOTO_READ_STATUS.WITH_IMAGES
              : PATIENT_PHOTO_READ_STATUS.FAILED,
          );
          return;
        }

        const { data, error } = await supabase
          .from("patient_profiles")
          .select("patient_photo_paths")
          .eq("user_id", user.id)
          .maybeSingle();

        if (error) {
          throw error;
        }

        const paths = Array.isArray(data?.patient_photo_paths)
          ? data.patient_photo_paths.filter((p) => String(p || "").trim())
          : [];

        if (!alive) return;
        if (!paths.length) {
          setImages([]);
          setPhotoReadStatus(PATIENT_PHOTO_READ_STATUS.EMPTY);
          clearPatientPhotosStorage(profile, user);
          return;
        }

        const signedRows = await Promise.all(
          paths.map(async (path, idx) => {
            const normalized = String(path || "").trim();
            if (!normalized) return null;

            const { data: signed, error: signedErr } = await supabase.storage
              .from(PATIENT_BUCKET)
              .createSignedUrl(normalized, 60 * 60 * 24);

            if (signedErr || !signed?.signedUrl) {
              throw signedErr || new Error("Patient photo signed URL is unavailable.");
            }
            return {
              id: `db-${idx}-${normalized}`,
              url: signed.signedUrl,
              path: normalized,
            };
          })
        );

        const next = signedRows.filter(Boolean);
        if (!next.length) throw new Error("Patient photo paths resolved without images.");
        if (!alive) return;

        setImages(next);
        setPhotoReadStatus(PATIENT_PHOTO_READ_STATUS.WITH_IMAGES);
        persistImagesLocal(next);
      } catch (err) {
        console.error("Patient photos hydrate failed", err);
        if (alive) setPhotoReadStatus(PATIENT_PHOTO_READ_STATUS.FAILED);
      }
    }

    hydrateFromDb();
    return () => {
      alive = false;
    };
  }, [persistImagesLocal]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleEnterEditMode = () => {
    setSelectionMode(true);
    if (images[0]) setSelectedIds(new Set([images[0].id]));
  };

  const handleExitEditMode = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const handleDeleteClick = () => {
    if (!selectedCount) return;
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedIds.size) return;

    const toDelete = images.filter((img) => selectedIds.has(img.id));
    const next = images.filter((img) => !selectedIds.has(img.id));

    setImages(next);
    setPhotoReadStatus(
      next.length
        ? PATIENT_PHOTO_READ_STATUS.WITH_IMAGES
        : PATIENT_PHOTO_READ_STATUS.EMPTY,
    );
    persistImagesLocal(next);

    setDeleteDialogOpen(false);
    setSelectionMode(false);
    setSelectedIds(new Set());
    setSnackbarMessage(selectedCount === 1 ? "Image deleted" : "Images deleted");
    setSnackbarOpen(true);

    try {
      setSyncing(true);

      const { default: supabase } = await import("@/services/supabaseClient");
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const pathsToDelete = toDelete
        .map((img) => String(img?.path || "").trim())
        .filter(Boolean);

      if (user?.id && pathsToDelete.length) {
        await supabase.storage.from(PATIENT_BUCKET).remove(pathsToDelete);
      }

      await persistPhotoPathsToDb(next);
    } catch (err) {
      console.error("Patient photo delete sync failed", err);
    } finally {
      setSyncing(false);
    }
  };

  const handleDownloadSelected = () => {
    if (!selectedCount) return;

    const selectedImages = images.filter((img) => selectedIds.has(img.id));
    if (!selectedImages.length) return;

    selectedImages.forEach((img, index) => {
      try {
        const link = document.createElement("a");
        link.href = img.url;

        const parts = (img.url || "").split("/");
        const last = parts[parts.length - 1] || "";
        const defaultName = `image-${index + 1}.jpg`;

        link.download = last || defaultName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (err) {
        console.error("Download failed", err);
        setSnackbarMessage("Could not download image");
        setSnackbarOpen(true);
      }
    });
  };

  const handleUploadFromDevice = () => {
    setBottomSheetOpen(false);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;

    const MAX = 7 * 1024 * 1024;
    const allowed = new Set(["image/jpeg", "image/jpg", "image/png", "image/webp"]);
    const invalid = files.find((f) => f.size > MAX || !allowed.has(String(f.type || "").toLowerCase()));
    if (invalid) {
      setSnackbarMessage("Use JPG, PNG or WebP under 7MB.");
      setSnackbarOpen(true);
      return;
    }

    try {
      setSyncing(true);

      const { default: supabase } = await import("@/services/supabaseClient");
      const {
        data: { user },
        error: authErr,
      } = await supabase.auth.getUser();

      if (authErr || !user?.id) {
        throw new Error("Please sign in to upload patient photos.");
      }

      const uploaded = await Promise.all(
        files.map(async (file, index) => {
          const ext = extFromFile(file);
          const path = `private/${user.id}/photos/${Date.now()}-${index}.${ext}`;

          const { error: upErr } = await supabase.storage
            .from(PATIENT_BUCKET)
            .upload(path, file, {
              upsert: true,
              contentType: file.type || undefined,
            });

          if (upErr) throw upErr;

          const { data: signed, error: signedErr } = await supabase.storage
            .from(PATIENT_BUCKET)
            .createSignedUrl(path, 60 * 60 * 24);

          if (signedErr || !signed?.signedUrl) {
            throw new Error("Could not preview uploaded image.");
          }

          return {
            id: `up-${Date.now()}-${index}`,
            path,
            url: signed.signedUrl,
          };
        })
      );

      const realExisting = images.filter((img) => !isDemoImageId(img.id));
      const next = [...uploaded, ...realExisting];
      setImages(next);
      setPhotoReadStatus(PATIENT_PHOTO_READ_STATUS.WITH_IMAGES);
      persistImagesLocal(next);
      await persistPhotoPathsToDb(next);

      setSnackbarMessage(uploaded.length === 1 ? "Image added" : "Images added");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Patient photo upload failed", err);
      setSnackbarMessage(err?.message || "Could not upload image.");
      setSnackbarOpen(true);
    } finally {
      setSyncing(false);
    }
  };

  const handleTakePhoto = () => {
    // Web fallback: use file picker (mobile can still pick camera)
    setBottomSheetOpen(false);
    fileInputRef.current?.click();
  };

  React.useEffect(() => {
    const action = String(location.state?.patientPhotosAction || "").trim();
    const actionKey = `${location.key}:${action}`;
    if (!action || handledEntryActionRef.current === actionKey) return;

    handledEntryActionRef.current = actionKey;
    if (action === "add") {
      setBottomSheetOpen(true);
      return;
    }
    if (action === "edit") {
      handleEnterEditMode();
    }
  }, [location.key, location.state]);

  // Keep local cache in sync (also used by doctor-side read path)
  React.useEffect(() => {
    if (
      photoReadStatus === PATIENT_PHOTO_READ_STATUS.LOADING ||
      photoReadStatus === PATIENT_PHOTO_READ_STATUS.FAILED
    ) {
      return;
    }
    persistImagesLocal(images);
  }, [images, persistImagesLocal, photoReadStatus]);

  const openViewerAt = (idx) => {
    setViewerIndex(idx);
    setViewerOpen(true);
  };

  const closeViewer = () => setViewerOpen(false);

  const goPrev = () => setViewerIndex((i) => (i <= 0 ? images.length - 1 : i - 1));
  const goNext = () => setViewerIndex((i) => (i >= images.length - 1 ? 0 : i + 1));

  return (
    <div className="w-full min-h-screen bg-[rgb(var(--afrimed-shell-background))]">
      <div className="afrimed-rail afrimed-rail--comfortable pb-10 pt-4">
        {/* Top bar */}
        <div className="flex items-center justify-between">
          {/* Left: Back / Close pill */}
          <button
            type="button"
            onClick={() => {
              if (selectionMode && selectedIds.size > 0) {
                setSelectedIds(new Set());
                setSelectionMode(false);
              } else {
                nav(-1);
              }
            }}
            className="h-10 px-3 inline-flex items-center justify-center rounded-[12px]"
            style={{
              background:
                "color-mix(in oklab, var(--Schemes-On-Surface,#040812) 8%, transparent)",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            <img
              src={selectionMode && selectedIds.size > 0 ? ICON_CLOSE : ICON_BACK}
              alt="Back"
              className={
                selectionMode && selectedIds.size > 0
                  ? "block h-5 w-5 translate-y-[2px] translate-x-[2px]"
                  : "block h-5 w-5"
              }
              draggable="false"
            />
          </button>

          {/* Right: Add + Edit pills (hidden in selection mode) */}
          {!selectionMode && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setBottomSheetOpen(true)}
                disabled={syncing}
                className="h-10 px-3 inline-flex items-center justify-center rounded-[12px]"
                style={{
                  background:
                    "color-mix(in oklab, var(--Schemes-On-Surface,#040812) 8%, transparent)",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                <img src={ICON_ADD} alt="Add" className="block h-5 w-5" />
              </button>

              <button
                type="button"
                onClick={handleEnterEditMode}
                disabled={syncing}
                className="h-10 px-3 inline-flex items-center justify-center rounded-[12px]"
                style={{
                  background:
                    "color-mix(in oklab, var(--Schemes-On-Surface,#040812) 8%, transparent)",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                <img
                  src={ICON_EDIT}
                  alt="Edit"
                  className="block h-5 w-5 translate-y-[2px] translate-x-[2px]"
                />
              </button>
            </div>
          )}
        </div>

        {/* Title / selection text */}
        <div className="mt-4 mb-4">
          {selectionMode && selectedCount > 0 ? (
            <h1 className="text-title-large text-on-surface">{selectedCount} Selected</h1>
          ) : (
            <h1 className="text-title-large text-on-surface">Patient photos</h1>
          )}
        </div>
        <PatientPhotoAccess />

        {photoReadStatus === PATIENT_PHOTO_READ_STATUS.LOADING ? (
          <Card className="mb-6 px-4 py-6 text-body-medium text-on-surface-var" role="status">
            Loading patient photos…
          </Card>
        ) : null}

        {photoReadStatus === PATIENT_PHOTO_READ_STATUS.EMPTY ? (
          <Card className="mb-6 px-4 py-6 text-body-medium text-on-surface-var">
            No patient photos shared yet.
          </Card>
        ) : null}

        {photoReadStatus === PATIENT_PHOTO_READ_STATUS.FAILED ? (
          <Card className="mb-6 px-4 py-6 text-body-medium text-on-surface-var" role="alert">
            Patient photos could not be loaded. Please try again.
          </Card>
        ) : null}

        {/* Grid */}
        <div className="grid grid-cols-2 gap-3 pb-20">
          {images.map((img, idx) => {
            const isSelected = selectedIds.has(img.id);
            return (
              <button
                key={img.id}
                type="button"
                onClick={() => {
                  if (selectionMode) toggleSelect(img.id);
                  else openViewerAt(idx);
                }}
                className="relative rounded-[28px] overflow-hidden bg-neutral-200 aspect-[1/1]"
              >
                <img src={img.url} alt="" className="h-full w-full object-cover" />
                {selectionMode && (
                  <div className="absolute top-2 right-2">
                    <div
                      className={`
                        h-6 w-6 rounded-full border border-white
                        flex items-center justify-center
                        ${isSelected ? "bg-primary" : "bg-[rgba(0,0,0,0.24)]"}
                      `}
                    >
                      {isSelected && (
                        <img src={ICON_CHECK} alt="Selected" className="h-4 w-4" />
                      )}
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Bottom app bar – selection mode */}
      {selectionMode && selectedCount > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-40 flex justify-center bg-[rgb(var(--clr-surface))]">
          <div className="w-full max-w-[720px] px-4">
            <div className="flex justify-between items-center border-t border-[rgb(var(--clr-outline-variant))] bg-[rgb(var(--clr-surface))] px-4 pt-3 pb-9">
              <button
                type="button"
                className="inline-flex items-center justify-center h-11 w-11 rounded-[16px]
                           bg-[color-mix(in_oklab,var(--Schemes-On-Surface,#040812)_8%,transparent)]"
                onClick={handleDownloadSelected}
                disabled={syncing}
              >
                <img src={ICON_DOWNLOAD_BOTTOM} alt="Download" className="h-5 w-5" />
              </button>

              <button
                type="button"
                className="inline-flex items-center justify-center h-11 w-11 rounded-[16px]
                           bg-[color-mix(in_oklab,var(--Schemes-Critical,#F8533A)_12%,transparent)]"
                onClick={handleDeleteClick}
                disabled={syncing}
              >
                <img src={ICON_DELETE_BOTTOM} alt="Delete" className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm dialog */}
      <BasicDialogCritical
        open={deleteDialogOpen}
        title={
          selectedCount === 1
            ? "Delete 1 image?"
            : `Delete ${selectedCount || ""} image${selectedCount > 1 ? "s" : ""}?`
        }
        body={
          selectedCount === 1
            ? "This image will be deleted from your photos. This action cannot be undone."
            : "These images will be deleted from your photos. This action cannot be undone."
        }
        cancelText="Cancel"
        okText="Delete"
        onCancel={() => setDeleteDialogOpen(false)}
        onOk={handleConfirmDelete}
      />

      {/* Snackbar */}
      <Snackbar open={!!snackbarOpen} onClose={() => setSnackbarOpen(false)}>
        {snackbarMessage}
      </Snackbar>

      {/* Bottom sheet – Add images */}
      {bottomSheetOpen && (
        <div className="fixed inset-0 z-40 flex items-end justify-center">
          <button
            type="button"
            className="absolute inset-0 bg-black/30"
            onClick={() => setBottomSheetOpen(false)}
          />

          <div className="relative w-full max-w-[720px] rounded-t-[28px] bg-[rgb(var(--clr-surface))] px-4 pt-3 pb-8">
            <div className="mx-auto h-1.5 w-12 rounded-full bg-[rgba(15,23,42,0.16)] mb-4" />
            <h2 className="text-title-medium text-on-surface mb-4">Add photos</h2>

            <div className="flex flex-col divide-y divide-[rgba(15,23,42,0.06)]">
              <button
                type="button"
                className="flex items-center justify-between py-3"
                onClick={handleUploadFromDevice}
                disabled={syncing}
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-white border border-[rgba(24,50,119,0.06)] flex items-center justify-center">
                    <img src={ICON_UPLOAD_IMAGE} alt="" className="h-5 w-5" />
                  </div>
                  <span className="text-body-large text-on-surface">Upload image</span>
                </div>
                <img src={ICON_CHEVRON_RIGHT} alt="" className="h-5 w-5" />
              </button>

              <button
                type="button"
                className="flex items-center justify-between py-3"
                onClick={handleTakePhoto}
                disabled={syncing}
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-white border border-[rgba(24,50,119,0.06)] flex items-center justify-center">
                    <img src={ICON_TAKE_PHOTO} alt="" className="h-5 w-5" />
                  </div>
                  <span className="text-body-large text-on-surface">Take Photo</span>
                </div>
                <img src={ICON_CHEVRON_RIGHT} alt="" className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Viewer (same “full” style you now love) */}
      {viewerOpen && images[viewerIndex] && (
        <div className="fixed inset-0 z-50 bg-black/60">
          {/* scrim click closes */}
          <button
            type="button"
            className="absolute inset-0"
            onClick={closeViewer}
            aria-label="Close"
          />

          {/* Rail-aligned header (matches your doctor-side final alignment) */}
          <div className="relative mx-auto w-full max-w-[720px] lg:max-w-[1008px] px-4 pt-5">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={closeViewer}
                className="h-10 w-10 rounded-[12px]
                           inline-flex items-center justify-center
                           bg-[color-mix(in_oklab,var(--Schemes-On-Surface,#040812)_8%,transparent)]"
                style={{ WebkitTapHighlightColor: "transparent" }}
              >
                <span className="text-white text-body-large translate-y-[2px] translate-x-[2px]">
                  ×
                </span>
              </button>

              <div className="text-white text-body-large">
                {viewerIndex + 1}/{images.length}
              </div>
            </div>
          </div>

          {/* image frame */}
          <div className="relative mx-auto mt-4 w-[calc(100vw-48px)] max-w-[1400px]">
            <div className="relative rounded-[28px] overflow-hidden bg-black">
              <img
                src={images[viewerIndex].url}
                alt=""
                className="w-full h-[70vh] object-contain bg-black"
                draggable="false"
              />

              {/* arrows outside the image edges */}
              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      goPrev();
                    }}
                    className="absolute left-[-35px] top-1/2 -translate-y-1/2
                               h-11 w-11 rounded-full
                               flex items-center justify-center"
                    style={{ WebkitTapHighlightColor: "transparent" }}
                  >
                    <img src={ICON_BEFORE} alt="Previous" className="h-6 w-6" />
                  </button>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      goNext();
                    }}
                    className="absolute right-[-35px] top-1/2 -translate-y-1/2
                               h-11 w-11 rounded-full
                               flex items-center justify-center"
                    style={{ WebkitTapHighlightColor: "transparent" }}
                  >
                    <img src={ICON_NEXT} alt="Next" className="h-6 w-6" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
